import { useRef, useState } from 'react'
import classNames from '@/utils/classNames'
import fileSizeUnit from '@/utils/fileSizeUnit'
import { PiArrowUp, PiFile, PiPaperclip, PiX } from 'react-icons/pi'
import type { ChangeEvent, KeyboardEvent } from 'react'

export type ChatInputProps = {
    placeholder?: string
    onInputChange?: (payload: { value: string; attachments: File[] }) => void
}

const MAX_TEXTAREA_HEIGHT = 160

/**
 * The chat's text field, attachment chip row and send button, in the
 * design's tokens. A rounded, auto-growing textarea replaces the old
 * single-line input and square hard-bordered box: Enter sends, Shift+Enter
 * inserts a newline, and the send button disables on empty/whitespace-only
 * input. The attachment behaviour itself (collect, list, remove) is
 * unchanged, and attachments still are not sent anywhere - `ChatView`'s
 * `handleInputChange` never reads them, same as before this pass.
 */
const ChatInput = (props: ChatInputProps) => {
    const [attachments, setAttachments] = useState<File[]>([])
    const [value, setValue] = useState('')

    const { placeholder, onInputChange } = props

    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const resize = () => {
        const el = textareaRef.current
        if (!el) return
        el.style.height = 'auto'
        el.style.height = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT) + 'px'
    }

    const handleInputClear = () => {
        setValue('')
        setAttachments([])
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
        requestAnimationFrame(resize)
    }

    const submit = () => {
        const trimmed = value.trim()
        if (!trimmed) return
        onInputChange?.({ value: trimmed, attachments })
        handleInputClear()
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            submit()
        }
    }

    const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files
        if (selected && selected.length > 0) {
            setAttachments((prev) => [...prev, ...Array.from(selected)])
        }
    }

    const removeAttachment = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index))
    }

    return (
        <div className="flex flex-col gap-2 px-4 pb-1 pt-2">
            {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {attachments.map((file, index) => (
                        <span
                            key={file.name + index}
                            className={classNames(
                                'inline-flex items-center gap-1.5 rounded-full border',
                                'border-[color:var(--rda-line)] bg-[var(--rda-neutral)]',
                                'py-1 pl-2.5 pr-1.5 text-xs text-[var(--rda-on-neutral)]',
                            )}
                        >
                            <PiFile aria-hidden className="shrink-0 text-sm" />
                            <span className="max-w-[140px] truncate">{file.name}</span>
                            <span className="shrink-0 text-[var(--rda-muted)]">
                                {fileSizeUnit(file.size)}
                            </span>
                            <button
                                type="button"
                                aria-label={`Remove ${file.name}`}
                                title={`Remove ${file.name}`}
                                className={classNames(
                                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                                    'text-[var(--rda-muted)] hover:bg-[var(--rda-surface)] hover:text-[var(--rda-on-surface)]',
                                    'outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-current',
                                )}
                                onClick={() => removeAttachment(index)}
                            >
                                <PiX aria-hidden />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div
                className={classNames(
                    'flex items-end gap-1 rounded-[22px] border bg-[var(--rda-surface)] py-1.5 pl-1.5 pr-2',
                    'border-[color:var(--rda-line)] transition-colors',
                    'focus-within:border-[color:var(--rda-accent-text)] focus-within:ring-1 focus-within:ring-[color:var(--rda-accent-text)]',
                )}
            >
                <input
                    ref={fileInputRef}
                    multiple
                    type="file"
                    className="hidden"
                    onChange={handleFilesSelected}
                />
                <button
                    type="button"
                    aria-label="Attach a file"
                    title="Attach a file"
                    className={classNames(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-lg',
                        'text-[var(--rda-muted)] transition-colors hover:text-[var(--rda-accent-text)]',
                        'outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-current',
                    )}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <PiPaperclip aria-hidden />
                </button>
                <textarea
                    ref={textareaRef}
                    rows={1}
                    value={value}
                    className="max-h-40 min-h-[32px] flex-1 resize-none bg-transparent py-1.5 text-[15px] leading-[1.4] text-[var(--rda-on-surface)] outline-none placeholder:text-[color:var(--rda-muted)]"
                    placeholder={placeholder}
                    onChange={(e) => {
                        setValue(e.target.value)
                        resize()
                    }}
                    onKeyDown={handleKeyDown}
                />
                <button
                    type="button"
                    aria-label="Send"
                    title="Send"
                    disabled={!value.trim()}
                    className={classNames(
                        'rda-tile flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                        'bg-[var(--rda-accent)] text-[var(--rda-on-accent)] transition-opacity',
                        'disabled:pointer-events-none disabled:opacity-40',
                    )}
                    onClick={submit}
                >
                    <PiArrowUp aria-hidden />
                </button>
            </div>
        </div>
    )
}

export default ChatInput
